import React from 'react';
import { Heart, Users, Shield, Globe, Award, Target, Lightbulb, Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutUsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">About TherapistDirect</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connecting individuals with compassionate mental health professionals to create a world where mental wellness is accessible to everyone.
          </p>
        </div>

        {/* Mission Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Heart className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              To bridge the gap between mental health professionals and those seeking support, making quality mental health care accessible, affordable, and stigma-free.
            </p>
          </div>
        </div>

        {/* Values Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Accessibility</h3>
            <p className="text-gray-600">
              We believe mental health support should be available to everyone, regardless of location, financial situation, or background.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Trust & Security</h3>
            <p className="text-gray-600">
              Your privacy and security are paramount. We maintain the highest standards of data protection and HIPAA compliance.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Award className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Quality</h3>
            <p className="text-gray-600">
              We carefully vet and verify all mental health professionals to ensure they meet our high standards of care and expertise.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Globe className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation</h3>
            <p className="text-gray-600">
              We leverage technology to create seamless connections and improve the mental health care experience for everyone.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-indigo-100 rounded-full">
                <Target className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Impact</h3>
            <p className="text-gray-600">
              We measure our success by the positive impact we have on individuals' mental health and well-being.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-pink-100 rounded-full">
                <Lightbulb className="h-6 w-6 text-pink-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation</h3>
            <p className="text-gray-600">
              We continuously evolve our platform to better serve the mental health community and address emerging needs.
            </p>
          </div>
        </div>

        {/* Story Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                TherapistDirect was founded in 2020 by a team of mental health professionals, technologists, and advocates who recognized the growing need for better access to mental health care. We saw that while the demand for mental health services was increasing, many people struggled to find the right provider, navigate insurance complexities, or overcome the stigma associated with seeking help.
              </p>
              <p className="mb-4">
                Our founders came together with a shared vision: to create a platform that would make finding and connecting with mental health professionals as simple as ordering food delivery. We wanted to remove the barriers that prevent people from getting the help they need and deserve.
              </p>
              <p className="mb-4">
                Today, TherapistDirect serves thousands of individuals and mental health professionals across the United States. We've built a comprehensive platform that not only connects people with providers but also supports the entire mental health ecosystem with tools for practice management, insurance verification, and patient engagement.
              </p>
              <p>
                We're proud of what we've accomplished, but we know our work is far from complete. Mental health care accessibility remains a critical challenge, and we're committed to continuing our mission of making quality mental health support available to everyone who needs it.
              </p>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Team</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Dr. Sarah Johnson</h3>
              <p className="text-gray-600 mb-2">Chief Executive Officer</p>
              <p className="text-sm text-gray-500">Licensed Clinical Psychologist with 15+ years of experience</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Michael Chen</h3>
              <p className="text-gray-600 mb-2">Chief Technology Officer</p>
              <p className="text-sm text-gray-500">Former tech leader with expertise in healthcare platforms</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Dr. Emily Rodriguez</h3>
              <p className="text-gray-600 mb-2">Chief Clinical Officer</p>
              <p className="text-sm text-gray-500">Board-certified psychiatrist and mental health advocate</p>
            </div>
          </div>
        </div>

        {/* Impact Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-white p-8 mb-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-6">Our Impact</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">50,000+</div>
                <p className="text-blue-100">Individuals Connected</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">2,500+</div>
                <p className="text-blue-100">Mental Health Professionals</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">95%</div>
                <p className="text-blue-100">Satisfaction Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Get in Touch</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <span className="text-gray-700">info@therapistdirect.org</span>
                </div>
                {/* <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <span className="text-gray-700">1-800-WELLNESS</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <span className="text-gray-700">123 Wellness St, Suite 400, Mental Health City, MH 12345</span>
                </div> */}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/find-therapist" className="block text-blue-600 hover:text-blue-800">
                  Find a Therapist
                </Link>
                <Link to="/blog" className="block text-blue-600 hover:text-blue-800">
                  Mental Health Blog
                </Link>
                <Link to="/contact-support" className="block text-blue-600 hover:text-blue-800">
                  Contact Support
                </Link>
                <Link to="/privacy" className="block text-blue-600 hover:text-blue-800">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 pt-8 mt-12 text-center">
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} TherapistDirect. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-blue-600 hover:text-blue-800">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-blue-600 hover:text-blue-800">
                Terms of Service
              </Link>
              <Link to="/accessibility" className="text-blue-600 hover:text-blue-800">
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;
